import type { ISODate } from '@interfaces/IScheduler';
import { getDay, parseISO } from 'date-fns';
import { addDays, daysBetween } from './timeScale';

export type WorkCalendar = (date: ISODate) => boolean; // true iff working day

export const ALL_DAYS_WORKING: WorkCalendar = () => true;

export const EXCLUDE_SUNDAY: WorkCalendar = (date) => getDay(parseISO(date)) !== 0;

export const EXCLUDE_WEEKENDS: WorkCalendar = (date) => {
  const dow = getDay(parseISO(date));
  return dow !== 0 && dow !== 6;
};

export function excludingDates(dates: ISODate[]): WorkCalendar {
  const set = new Set(dates);
  return (date) => !set.has(date);
}

export function combine(...calendars: WorkCalendar[]): WorkCalendar {
  return (date) => calendars.every((calendarFn) => calendarFn(date));
}

// Count working days in the inclusive range [from, to].
// Returns 0 when from > to.
export function workingDaysBetween(from: ISODate, to: ISODate, calendar: WorkCalendar): number {
  const span = daysBetween(from, to);
  if (span < 0) return 0;
  // Fast path: with no non-working days there's nothing to skip, so the count is
  // simply the inclusive span. This avoids a per-day `addDays` loop whose date-fns
  // parse+format is O(span) per task — on a multi-thousand-task schedule (CPM over
  // the whole graph) that loop alone costs ~1.7s and stalls every area edit.
  if (calendar === ALL_DAYS_WORKING) return span + 1;
  let n = 0;
  for (let i = 0; i <= span; i++) {
    if (calendar(addDays(from, i))) n++;
  }
  return n;
}

// Advance from `date` by `n` working days. n=0 returns `date` itself (regardless of whether it's a working day).
// n>0: skip non-working days while counting.
export function addWorkingDays(date: ISODate, n: number, calendar: WorkCalendar): ISODate {
  if (n === 0) return date;
  let cursor = date;
  let remaining = Math.abs(n);
  const step = n > 0 ? 1 : -1;
  while (remaining > 0) {
    cursor = addDays(cursor, step);
    if (calendar(cursor)) remaining--;
  }
  return cursor;
}

// Snap forward to the next working day (or stay if already working).
export function nextWorkDay(date: ISODate, calendar: WorkCalendar): ISODate {
  let cursor = date;
  while (!calendar(cursor)) cursor = addDays(cursor, 1);
  return cursor;
}

// A minimal 2026 Japanese national holiday set, useful for the demo mock.
// Real production usage should source this from a backend calendar service.
export const JP_HOLIDAYS_2026: ISODate[] = [
  '2026-01-01', // 元日
  '2026-01-12', // 成人の日
  '2026-02-11', // 建国記念の日
  '2026-02-23', // 天皇誕生日
  '2026-03-20', // 春分の日
  '2026-04-29', // 昭和の日
  '2026-05-03', // 憲法記念日
  '2026-05-04', // みどりの日
  '2026-05-05', // こどもの日
  '2026-05-06', // 振替休日
  '2026-07-20', // 海の日
  '2026-08-11', // 山の日
  '2026-09-21', // 敬老の日
  '2026-09-22', // 国民の休日
  '2026-09-23', // 秋分の日
  '2026-10-12', // スポーツの日
  '2026-11-03', // 文化の日
  '2026-11-23', // 勤労感謝の日
];
